import { useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Power, Lock, Crown, Shield, User as UserIcon, Building2, Copy, UserCog } from "lucide-react";

const ROLE_META = {
  owner: { label: "Dueño", icon: Crown, color: "bg-amber-100 text-amber-800" },
  administrador: { label: "Administrador", icon: Shield, color: "bg-violet-100 text-violet-800" },
  supervisor: { label: "Supervisor", icon: Shield, color: "bg-blue-100 text-blue-800" },
  usuario: { label: "Usuario", icon: UserIcon, color: "bg-slate-100 text-slate-700" },
};

function genPassword() {
  const charset = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 10 }, () => charset[Math.floor(Math.random() * charset.length)]).join("");
}

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tempPasswordDialog, setTempPasswordDialog] = useState(null);

  const isAdmin = ["owner", "administrador"].includes(user?.role);
  const canCreate = ["owner", "administrador", "supervisor"].includes(user?.role);
  const isEmpresa = company?.license_type === "empresa";

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/team/users"), api.get("/company")]);
      setUsers(u.data);
      setCompany(c.data);
    } catch (e) {
      toast.error("Error cargando equipo");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (form) => {
    try {
      await api.post("/team/users", form);
      toast.success("Usuario creado");
      setTempPasswordDialog({ email: form.email, password: form.temp_password });
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear usuario");
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/team/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const handleResetPassword = async (u) => {
    try {
      const { data } = await api.post(`/team/users/${u.id}/reset-password`);
      setTempPasswordDialog({ email: u.email, password: data.temp_password });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/team/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const supervisors = users.filter((u) => ["administrador", "owner", "supervisor"].includes(u.role));

  if (loading) return <div className="text-slate-500 text-sm">Cargando equipo…</div>;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">Equipo</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestiona los usuarios con acceso a tu cuenta empresa.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEmpresa && (
            <Badge className="bg-violet-100 text-violet-800 border-0 px-3 py-1">
              <Building2 size={12} className="mr-1" /> Licencia Empresa · {users.length}/{company?.user_quota || 1}
            </Badge>
          )}
          {!isEmpresa && isAdmin && (
            <Button onClick={() => setShowUpgrade(true)} variant="outline" data-testid="upgrade-empresa-btn">
              <Building2 size={16} className="mr-1" /> Activar licencia Empresa
            </Button>
          )}
          {isEmpresa && canCreate && (
            <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-user-btn">
              <UserPlus size={16} className="mr-1" /> Crear usuario
            </Button>
          )}
        </div>
      </div>

      {!isEmpresa && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-5 text-sm text-amber-900">
          <strong className="block mb-1">Estás en licencia Individual</strong>
          Para invitar a tu equipo, activa la licencia Empresa con cupo configurable y crea usuarios con
          roles <em>Administrador</em>, <em>Supervisor</em> y <em>Usuario</em>.
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Rol</th>
                <th className="text-left px-4 py-3">Supervisor</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.usuario;
                const Icon = meta.icon;
                const sup = users.find((x) => x.id === u.supervisor_id);
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {u.name}
                      {u.id === user.id && <span className="ml-2 text-xs text-blue-600">(tú)</span>}
                      {u.must_change_password && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                          Pendiente 1er login
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${meta.color}`}>
                        <Icon size={12} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{sup?.name || "—"}</td>
                    <td className="px-4 py-3">
                      {u.is_active === false ? (
                        <span className="text-xs text-red-600 font-medium">Desactivado</span>
                      ) : (
                        <span className="text-xs text-emerald-600 font-medium">Activo</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canCreate && u.id !== user.id && u.role !== "owner" && (
                          <>
                            <Button size="sm" variant="ghost" title="Resetear contraseña" onClick={() => handleResetPassword(u)} data-testid={`reset-pw-${u.id}`}>
                              <KeyRound size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" title={u.is_active === false ? "Activar" : "Desactivar"} onClick={() => handleToggleActive(u)} data-testid={`toggle-${u.id}`}>
                              <Power size={14} />
                            </Button>
                          </>
                        )}
                        {isAdmin && u.id !== user.id && u.role !== "owner" && (
                          <>
                            <Button size="sm" variant="ghost" title="Cambiar rol / supervisor" onClick={() => setEditingUser(u)} data-testid={`edit-role-${u.id}`}>
                              <UserCog size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" title="Eliminar" onClick={() => handleDelete(u)} className="text-red-600" data-testid={`delete-${u.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showUpgrade && (
        <UpgradeDialog
          onClose={() => setShowUpgrade(false)}
          onDone={(quota) => { setShowUpgrade(false); toast.success(`Licencia Empresa activada (cupo ${quota})`); load(); }}
        />
      )}
      {showCreate && (
        <CreateUserDialog
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          supervisors={supervisors}
          currentUserRole={user.role}
          currentUserId={user.id}
        />
      )}
      {tempPasswordDialog && (
        <TempPasswordDialog data={tempPasswordDialog} onClose={() => setTempPasswordDialog(null)} />
      )}
      {editingUser && (
        <EditUserDialog
          userToEdit={editingUser}
          supervisors={supervisors.filter(s => s.id !== editingUser.id)}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); toast.success("Usuario actualizado"); load(); }}
        />
      )}
    </div>
  );
}

function UpgradeDialog({ onClose, onDone }) {
  const [quota, setQuota] = useState(5);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/company/upgrade-empresa", { user_quota: parseInt(quota, 10) });
      onDone(quota);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Activar licencia Empresa</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Tu cuenta pasará a licencia <strong>Empresa</strong>. Podrás crear usuarios con roles
            Administrador, Supervisor y Usuario hasta el cupo definido.
          </p>
          <Label>Cupo de usuarios (incluyéndote a ti)</Label>
          <Input type="number" min={1} value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="upgrade-quota-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="upgrade-confirm-btn">
            {saving ? "Activando…" : "Activar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ onClose, onSubmit, supervisors, currentUserRole, currentUserId }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "usuario",
    supervisor_id: currentUserRole === "supervisor" ? currentUserId : "",
    temp_password: genPassword(),
  });
  const isAdmin = ["owner", "administrador"].includes(currentUserRole);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.name) return toast.error("Completa email y nombre");
    if (form.role === "usuario" && isAdmin && !form.supervisor_id) return toast.error("Selecciona un supervisor");
    onSubmit({
      email: form.email,
      name: form.name,
      role: form.role,
      supervisor_id: form.role === "usuario" ? form.supervisor_id : null,
      temp_password: form.temp_password,
    });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Crear usuario</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <Label>Nombre completo</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-user-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-user-email" />
          </div>
          <div>
            <Label>Rol</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isAdmin && <SelectItem value="supervisor">Supervisor</SelectItem>}
                <SelectItem value="usuario">Usuario</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "usuario" && isAdmin && (
            <div>
              <Label>Supervisor asignado</Label>
              <Select value={form.supervisor_id} onValueChange={(v) => set("supervisor_id", v)}>
                <SelectTrigger data-testid="new-user-supervisor"><SelectValue placeholder="Selecciona un supervisor" /></SelectTrigger>
                <SelectContent>
                  {supervisors.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({ROLE_META[s.role].label})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Contraseña temporal</Label>
            <div className="flex gap-2">
              <Input value={form.temp_password} onChange={(e) => set("temp_password", e.target.value)} data-testid="new-user-temp-password" />
              <Button type="button" variant="outline" onClick={() => set("temp_password", genPassword())}>Generar</Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">El usuario deberá cambiarla al iniciar sesión por primera vez.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-new-user">Crear usuario</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ data, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${data.email}\nContraseña temporal: ${data.password}`);
    toast.success("Copiado al portapapeles");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock size={18} /> Contraseña temporal</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Entrega estas credenciales al usuario. El sistema le pedirá cambiar la contraseña al iniciar
            sesión por primera vez. <strong>Esta contraseña no se volverá a mostrar.</strong>
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-sm space-y-1">
            <div><span className="text-slate-500">Email: </span>{data.email}</div>
            <div><span className="text-slate-500">Contraseña: </span><strong>{data.password}</strong></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy}><Copy size={14} className="mr-1" /> Copiar</Button>
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ userToEdit, supervisors, onClose, onSaved }) {
  const [role, setRole] = useState(userToEdit.role);
  const [supervisorId, setSupervisorId] = useState(userToEdit.supervisor_id || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (role === "usuario" && !supervisorId) {
      return toast.error("Debes asignar un supervisor para el rol Usuario");
    }
    setSaving(true);
    try {
      const payload = { role };
      if (role === "usuario") payload.supervisor_id = supervisorId;
      await api.put(`/team/users/${userToEdit.id}`, payload);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog size={18} /> Cambiar rol de {userToEdit.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Ajusta el rol dentro de la empresa. Solo un <strong>administrador</strong> puede promover
            o degradar a otros usuarios.
          </p>
          <div>
            <Label>Rol</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="administrador">Administrador</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="usuario">Usuario</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === "usuario" && (
            <div>
              <Label>Supervisor asignado</Label>
              <Select value={supervisorId} onValueChange={setSupervisorId}>
                <SelectTrigger data-testid="edit-user-supervisor"><SelectValue placeholder="Selecciona un supervisor" /></SelectTrigger>
                <SelectContent>
                  {supervisors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({(ROLE_META[s.role] || {}).label || s.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-user">
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

